import { currentDatabaseSelector, selectedStateHighwaysSubnetworkState } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import { useE3PeerGroups } from '@src/modules/performance/context/reports-api-hooks';
import { SPLIT_CIRCLE_COLOURS as COLOURS, e3Legend } from '@src/modules/performance/shared/constants';
import {
  Box,
  Center,
  Table, TableCaption, Tbody, Td, Text, Th, Thead, Tr,
} from '@chakra-ui/react';
import IndicatorLegend from '@src/modules/performance/components/IndicatorLegend';
import ReportSection from './ReportSection';

const colourMap = {
  1: COLOURS.LIGHT_BLUE,
  2: COLOURS.BLUE,
  3: COLOURS.DARK_BLUE,
  default: 'GREY',
};

const labelMap = {
  economy: 'Value for Money',
  effectiveness: 'Effectiveness',
  efficiency: 'Efficiency',
};

const informationSource = {
  title: null,
  content: [
    {
      title: 'Comparative',
      description:
  <Text fontSize="inherit" mb={2}>
    The indicators for the list of networks that are peers of the selected network. RCAs are peer grouped with similar RCAs based on a combination of urban/rural split and traffic volume in their network. Regions are all compared to each other in a single peer group.
    <br />
    Network peers are sorted in the following order based on their results: Value for Money (highest to lowest), then Efficiency (highest to lowest), then Effectiveness (highest to lowest).
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Calculated by Road Efficiency Group within Transport Insights.
  </Text>,
    },
  ],
};

function E3PeerGroup() {
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: peerGroupData, isLoading: isLoadingPeerGroup } = useE3PeerGroups();
  const sectionTitle = 'Comparative';
  const selectedSubnetwork = useRecoilValue(selectedStateHighwaysSubnetworkState);

  const peerGroupLockYear = peerGroupData?.at(-1)?.lockYear;

  // Sort peer group data by economy, efficiency, effectiveness, then alphabetically by roadCouncil
  const orderedPeerGroupData = peerGroupData?.sort((a, b) => {
    // Sort by economy (descending: 3 is best, 1 is lowest)
    if (a.data.economy !== b.data.economy) {
      return b.data.economy - a.data.economy;
    }
    // Sort by efficiency if economy is the same
    if (a.data.efficiency !== b.data.efficiency) {
      return b.data.efficiency - a.data.efficiency;
    }
    // Sort by effectiveness if economy and efficiency are the same
    if (a.data.effectiveness !== b.data.effectiveness) {
      return b.data.effectiveness - a.data.effectiveness;
    }
    // Sort alphabetically by roadCouncil if all other criteria are the same
    return (a.roadCouncil || '').localeCompare(b.roadCouncil || '');
  });

  return (
    <ReportSection
      title={sectionTitle}
      lockYear={peerGroupLockYear}
      isLoading={isLoadingPeerGroup}
      informationSource={informationSource}
      height="100%"
      contentProps={{ justify: 'flex-start' }}
    >
      {orderedPeerGroupData?.length > 0 && (
      <Table
        variant="simple"
        width="100%"
        sx={{
          'td:nth-of-type(1)': {
            borderRight: 'solid 2px lightgray',
          },
        }}
        size="sm"
      >
        <Thead>
          <Tr>
            <Th textAlign="right" fontSize="xs" px={0}>
              &nbsp;
            </Th>
            <Th textAlign="center" fontSize="xs" lineHeight="1" px={1} py={2}>
              {labelMap.economy}
            </Th>
            <Th textAlign="center" fontSize="xs" px={1} py={2}>
              {labelMap.efficiency}
            </Th>
            <Th textAlign="center" fontSize="xs" px={1} py={2}>
              {labelMap.effectiveness}
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {orderedPeerGroupData?.map((item) => (
            <Tr
              key={`row-${item.roadCouncil}`}
              backgroundColor={(item.rcaId === 81 && item.subNetworkId === selectedSubnetwork) || (item.rcaId !== 81 && currentDatabase.id === item.rcaId) ? 'gray.50' : null}
              borderBottom="1px solid"
              borderColor="gray.100"
            >
              <Td border="none" py={1}>
                <Text
                  align="right"
                  color={(item.rcaId === 81 && item.subNetworkId === selectedSubnetwork) || (item.rcaId !== 81 && currentDatabase.id === item.rcaId) ? 'orange.500' : null}
                  fontWeight={(item.rcaId === 81 && item.subNetworkId === selectedSubnetwork) || (item.rcaId !== 81 && currentDatabase.id === item.rcaId) ? 'bold' : 'normal'}
                  fontSize="xs"
                  lineHeight="1"
                >
                  {item.roadCouncil}
                </Text>
              </Td>
              <Td border="none" px={0} py={1}>
                <Center>
                  <Box borderRadius="full" width="16px" height="16px" backgroundColor={colourMap[item.data.economy]} />
                </Center>
              </Td>
              <Td border="none" px={0} py={1}>
                <Center>
                  <Box borderRadius="full" width="16px" height="16px" backgroundColor={colourMap[item.data.efficiency]} />
                </Center>
              </Td>
              <Td border="none" px={0} py={1}>
                <Center>
                  <Box borderRadius="full" width="16px" height="16px" backgroundColor={colourMap[item.data.effectiveness]} />
                </Center>
              </Td>
            </Tr>
          ))}
        </Tbody>
        <TableCaption textAlign="center" mt={2}>
          <IndicatorLegend items={e3Legend} />
        </TableCaption>
      </Table>
      )}
      {orderedPeerGroupData?.length === 0 && !isLoadingPeerGroup && (
      <Text fontSize="sm" textAlign="center" my={4}>
        No data available
      </Text>
      )}
    </ReportSection>
  );
}

export default E3PeerGroup;
